'use client';

import type { PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import { Slot } from './Slot/Slot';
import { useCombinedRefs, useImpressionRef } from '../hooks';
export interface ImpressionAreaProps extends Omit<IntersectionObserverInit, 'threshold'> {
  onImpressionStart?: () => void;
  onImpressionEnd?: () => void;
  /**
   * Determines what percentage of the element needs to be visible to be considered "visible".
   * For example, if 0.5 is given, onImpressionStart is called when 50% or more is visible,
   * and onImpressionEnd is called when less than 50% is visible.
   * @default 0
   */
  areaThreshold?: number;
  /**
   * Determines how many milliseconds an element must be visible or hidden before updating the value.
   * For example, if the value is 2000, events won't be called if the element's visibility state
   * changes within 2 seconds but returns to its original state.
   * @default 0
   */
  timeThreshold?: number;
  /**
   * If true, onImpressionStart and onImpressionEnd will only be called once each for the lifetime of the component.
   * Subsequent visibility changes will not trigger these callbacks again.
   * @default false
   */
  impressionOnce?: boolean;
  className?: string;
  style?: React.CSSProperties;
  role?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean | 'false' | 'true';
}
export const ImpressionArea = forwardRef<HTMLDivElement, PropsWithChildren<ImpressionAreaProps>>(({
  root,
  rootMargin,
  areaThreshold,
  timeThreshold,
  impressionOnce,
  className,
  children,
  onImpressionStart,
  onImpressionEnd,
  style,
  ...otherProps
}, forwardedRef) => {
  const impressionRef = useImpressionRef<HTMLDivElement>({
    onImpressionStart,
    onImpressionEnd,
    areaThreshold,
    timeThreshold,
    impressionOnce,
    root,
    rootMargin
  });
  const ref = useCombinedRefs(forwardedRef, impressionRef);
  return <Slot className={className} ref={ref} style={style} {...otherProps}>
        {children}
      </Slot>;
});
ImpressionArea.displayName = 'ImpressionArea';